'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { FiSend } from 'react-icons/fi';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });

      // mark form submit to gtag
      if (window.ga) {
        window.ga('send', 'event', 'form', 'submit', 'contact');
      }

      if (window.gtag) {
        window.gtag('event', 'submit', {
          event_category: 'form',
          event_label: 'contact',
        });
      }

      if (window.fbq) {
        window.fbq('track', 'Contact');
      }

      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'mb-8 grid min-h-fit w-full max-w-[840px] grid-cols-12 gap-x-4 gap-y-6 sm:grid-cols-12 md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12 2xl:grid-cols-12',
          'ea-price-calculator-form__form',
        )}
      >
        <FormField
          control={form.control}
          name={'name'}
          render={({ field }) => (
            <FormItem
              required
              className={cn(
                'col-span-6 flex w-full flex-col items-start justify-start gap-1.5',
                'ea-contact-form__field ea-contact-form__name-field',
              )}
            >
              <FormLabel>
                <span>{t('text33')}</span>
              </FormLabel>
              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__name-input-wrapper',
                )}
              >
                <FormControl>
                  <Input
                    {...(field as any)}
                    placeholder={t('text33') + ' *'}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                    className={cn('', 'ea-contact-form__input ea-contact-form__name-input')}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'surname'}
          render={({ field }) => (
            <FormItem
              required
              className={cn(
                'col-span-6 flex w-full flex-col items-start justify-start gap-1.5',
                'ea-contact-form__surname-field ea-contact-form__field',
              )}
            >
              <FormLabel>
                <span>{t('text34')}</span>
              </FormLabel>
              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__surname-input-wrapper',
                )}
              >
                <FormControl>
                  <Input
                    {...(field as any)}
                    placeholder={t('text34') + ' *'}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                    className={cn('', 'ea-contact-form__input ea-contact-form__surname-input')}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'email'}
          render={({ field }) => (
            <FormItem
              required
              className={cn('col-span-12', 'ea-contact-form__email-field ea-contact-form__field')}
            >
              <FormLabel>
                <span>{t('text35')}</span>
              </FormLabel>
              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__email-input-wrapper',
                )}
              >
                <FormControl>
                  <Input
                    {...(field as any)}
                    type="email"
                    placeholder={t('text259') + ' *'}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                    className={cn('', 'ea-contact-form__email-input ea-contact-form__input')}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'text'}
          render={({ field }) => (
            <FormItem
              required
              className={cn('col-span-12', 'ea-contact-form__field ea-contact-form__text-field')}
            >
              <FormLabel>
                <span>{t('text80')}</span>
              </FormLabel>
              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper',
                )}
              >
                <FormControl>
                  <Textarea
                    placeholder={t('text615') + ' *'}
                    {...(field as any)}
                    rows={5}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'col-span-12 flex items-center gap-2',
                'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
              )}
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                />
              </FormControl>
              <FormLabel className={cn('', 'ea-contact-form__gdpr-checkbox-label')}>
                {t('text613')}{' '}
                <Link
                  href={ROUTES.GDPR()}
                  className={cn('', 'ea-contact-form__gdpr-link')}
                  prefetch={false}
                >
                  <span className="text-primary">{t('text614')}</span>
                </Link>
              </FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12')}>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant={CONTACT_CONFIG.form.submit.variant}
          className={cn('col-span-12', 'ea-contact-form__submit')}
          isLoading={form.formState.isSubmitting}
        >
          <FiSend className="mr-2 h-5 w-5" />
          {t('text185')}
        </Button>
      </form>
    </Form>
  );
}
